// src/sim/hitbox.ts — Hitbox dünya konumu (ARCHITECTURE §4.5): MoveDef/MoveStage klip zamanı → clipdata eklem eğrisi → yaw ile dünyaya;
// küre–kapsül (dik / yatan hurtbox) ve koni testleri. `hitboxWorld` SimSystems.hitboxWorld (HitboxLocator) imzasıyla dışa verilir;
// F3 debug çizimi (WP8) aynı fonksiyonu çağırır → görsel-sim tek kaynak. three'siz, tahsissiz (scratch vektör).
//
// Zaman tabanı: stage tick'i = moveTick − (önceki aşamaların ticks toplamı) — HAMLE tick'i cinsinden (hitstop'ta moveTick durur, dünya
// tick'i değil). t = clipStart + stageTick·DT·rate (kanonik klip sn; dilim eksen kaydırmaz). landSyncTime'lı hamlede inişten sonra
// (phase ≥ Active) t = landSyncTime + (moveTick − windup)·DT·rate. pauseAt → t = min(t, pauseAt); clipEnd/t1 → clamp.
import { MovePhase } from '../core/types.ts';
import type { ClipDataSet, Fighter, HitboxDef, MoveDef, MoveStage, SimWorld, Vec3 } from '../core/types.ts';
import { localToWorld, v3 } from '../core/vec3.ts';
import { HURT_R, HURT_Y0, HURT_Y1, LYING_HURT_HALF, LYING_HURT_Y, TICK_DT } from '../config/constants.ts';
import { FState } from '../core/types.ts';

/** 2B uzunluk: Math.hypot V8'de FixedDoubleArray tahsis eder (sıcak yol yasağı; match.ts kalıbı) → sqrt(a²+b²). */
const hyp = (a: number, b: number): number => Math.sqrt(a * a + b * b);

const LOCAL: Vec3 = v3();

/** Aşamanın başladığı hamle tick'i: Σ ticks (önceki aşamalar). stages yoksa 0. */
export function stageStartMoveTick(m: MoveDef, stageIdx: number): number {
  if (!m.stages) return 0;
  let t = 0;
  for (let i = 0; i < stageIdx && i < m.stages.length; i++) t += m.stages[i]!.ticks;
  return t;
}
/** moveTick'e göre aşama indeksi (son aşama −1 = hamle sonuna kadar). */
export function stageIndexFor(m: MoveDef, moveTick: number): number {
  if (!m.stages) return 0;
  let start = 0;
  for (let i = 0; i < m.stages.length - 1; i++) {
    const tk = m.stages[i]!.ticks;
    if (moveTick < start + tk) return i;
    start += tk;
  }
  return m.stages.length - 1;
}
/** Aşama (ya da hamlenin kendisi) — clip/clipStart/clipRate/clipEnd/pauseAt kaynağı. */
export function stageOf(m: MoveDef, stageIdx: number): MoveStage | MoveDef {
  return m.stages ? m.stages[Math.min(stageIdx, m.stages.length - 1)]! : m;
}
/** Hangi hamle tablosunun HANGİ klip kümesiyle çözüldüğü.
 *  NEDEN: `clipIdx`/`clipEnd` doğrudan paylaşılan `MoveDef`/`MoveStage` NESNELERİNE yazılıyor ve `world.moves`
 *  varsayılanı süreç-global `MOVES` tablosu (`world.ts` `o.moves ?? MOVES`). Aynı süreçte FARKLI `ClipDataSet`
 *  taşıyan iki dünya (sunucu + istemci, ya da gerçek + sentetik klipler) birbirinin indekslerini SESSİZCE ezer:
 *  ikinci dünya çözünce birincinin hitbox'ları yanlış klibe bakmaya başlar ve hiçbir hata çıkmaz. Ağ işi aynı
 *  süreçte birden çok dünya koşturacağı için (NETWORK.md §5) bu sessiz bozulma gürültülü hataya çevrildi.
 *  Bu WeakMap sim DURUMU DEĞİL — yalnız bir koruma: determinizmi etkilemez, `hash()`e girmez, tick'te okunmaz. */
const resolvedWith = new WeakMap<readonly MoveDef[], ClipDataSet>();
/** Son doğrulanan (tablo, klip) çifti — hızlı yol. Dünyalar tek tek adımlandığı için bu önbellek neredeyse her
 *  zaman tutuyor ve sıcak yolda WeakMap araması yapılmıyor. Sim DURUMU değil: yalnız kontrolün atlanıp
 *  atlanmayacağını belirliyor, sonucu değiştirmiyor. */
let lastMoves: readonly MoveDef[] | null = null;
let lastClips: ClipDataSet | null = null;
function claimMoveTable(world: SimWorld): void {
  if (world.moves === lastMoves && world.clips === lastClips) return;
  const prev = resolvedWith.get(world.moves);
  if (prev === undefined) { resolvedWith.set(world.moves, world.clips); lastMoves = world.moves; lastClips = world.clips; return; }
  if (prev === world.clips) { lastMoves = world.moves; lastClips = world.clips; return; }
  throw new Error(
    'hitbox: aynı hamle tablosu iki farklı ClipDataSet ile çözülüyor. clipIdx/clipEnd paylaşılan MoveDef ' +
    'nesnelerinde tutulduğu için ilk dünyanın indeksleri sessizce bozulurdu. Aynı süreçteki dünyalar ya aynı ' +
    'ClipDataSet\'i paylaşmalı ya da kendi moves tablosunu taşımalı (NETWORK.md §5).');
}

/** clipIdx tembel çözümü (yükleme sonunda WP9 `resolveMoveClips` çağırırsa indeks zaten çözülmüştür).
 *  Koruma KOŞULSUZ çağrılıyor: `if (st.clipIdx < 0)` içine konursa, bir dünya tabloyu çözdükten sonra FARKLI klip
 *  kümesiyle gelen ikinci dünya dalın içine hiç girmez ve birincinin indekslerini sessizce kullanır — ölçüldü ve
 *  bu delik gerçekti. Maliyeti `claimMoveTable`ın hızlı yolu (iki referans karşılaştırması). */
export function clipIdxOf(world: SimWorld, st: MoveStage | MoveDef): number {
  claimMoveTable(world);
  if (st.clipIdx < 0) st.clipIdx = world.clips.indexOf(st.clip);
  return st.clipIdx;
}
/** Aşama/hamle: clipIdx çözümü + clipEnd boşsa (dilimsiz klip) kanonik klip süresi → moveClipTime her zaman sabit üst sınırla kırpar. */
function resolveStage(world: SimWorld, st: MoveStage | MoveDef): void {
  st.clipIdx = world.clips.indexOf(st.clip);
  if (st.clipEnd === undefined && st.clipIdx >= 0) st.clipEnd = world.clips.byIndex(st.clipIdx).duration;
}
/** Yükleme sonunda tüm hamle/aşama clipIdx'lerini (ve boş clipEnd'leri) çözer (WP9). */
export function resolveMoveClips(world: SimWorld): void {
  claimMoveTable(world);
  for (let i = 0; i < world.moves.length; i++) {
    const m = world.moves[i]!;
    resolveStage(world, m);
    if (m.stages) for (const s of m.stages) resolveStage(world, s);
  }
}

/** Bu tick'in kanonik klip zamanı (sn) — animMap senkronu da aynı formülü kullanabilir. */
export function moveClipTime(m: MoveDef, f: Fighter): number {
  const st = stageOf(m, f.stageIdx);
  let t: number;
  if (m.landSyncTime !== undefined && f.phase >= MovePhase.Active) t = m.landSyncTime + (f.moveTick - m.windup) * TICK_DT * st.clipRate;
  else t = st.clipStart + (f.moveTick - stageStartMoveTick(m, f.stageIdx)) * TICK_DT * st.clipRate;
  const pause = st.pauseAt ?? m.pauseAt;
  if (pause !== undefined && t > pause) t = pause;
  // clipEnd: dilimli klipte config/moves.ts yükleme anında t1'i yazar; dilimsizde resolveMoveClips kanonik süreyi yazar
  // (parseClipName burada YOK — sıcak yolda tahsis yapmaz).
  const end = st.clipEnd;
  if (end !== undefined && t > end) t = end;
  return t < 0 ? 0 : t;
}

/** Silah uzunluğu (uç eklemleri için): sağ el weaponItem, sol el weaponItemL (dual) ya da kalkan (shieldItem). */
function weaponLenFor(world: SimWorld, f: Fighter, joint: HitboxDef['joint']): number {
  const left = joint === 'weapon.tip.l' || joint === 'weapon.mid.l';
  const id = left ? (f.weaponItemL >= 0 ? f.weaponItemL : f.shieldItem) : f.weaponItem;
  if (id < 0) return 0;
  const it = world.items[id];
  return it ? it.length : 0;
}

/**
 * HitboxLocator: küre merkezi out'a, yarıçap döner. Eklem: clipdata (fwd, up MUTLAK, side) + offset; center: yalnız offset.
 * Hamle yoksa (moveIdx < 0) kapsül merkezi döner (F3 çizimi güvenli).
 */
export function hitboxWorld(out: Vec3, world: SimWorld, f: Fighter, hb: HitboxDef): number {
  const m = f.moveIdx >= 0 ? world.moves[f.moveIdx] : undefined;
  let fwd = 0, up = 0, side = 0;
  if (hb.joint !== 'center' && m) {
    const st = stageOf(m, f.stageIdx);
    const ci = clipIdxOf(world, st);
    if (ci >= 0) {
      world.clips.jointAt(LOCAL, ci, hb.joint, moveClipTime(m, f), weaponLenFor(world, f, hb.joint));
      fwd = LOCAL.x; up = LOCAL.y; side = LOCAL.z;
    }
  }
  if (hb.offset) { fwd += hb.offset[0]; up += hb.offset[1]; side += hb.offset[2]; }
  localToWorld(out, f.pos, f.yaw, fwd, up, side);
  return hb.radius;
}

/** Hedef yatıyor mu (yatay hurtbox): Lying / Crawl / Roll / Knockdown. */
export const isLyingState = (s: FState): boolean =>
  s === FState.Lying || s === FState.Crawl || s === FState.Roll || s === FState.Knockdown;

/** Küre – dövüşçü hurtbox'ı (dik kapsül r 0.45 y ∈ [0.1, 2.2] | yatan: yatay kapsül y 0.35, ±0.8 ileri) — segman üzerindeki en yakın nokta.
 *  Merkez Vec3 ile: double alanlar tagged argüman olarak geçince V8 her birini HeapNumber'a kutular (4 × 16 B / hedef / tick — resolveAttacker
 *  en büyük sim tahsis kaynağıydı); nesne referansı geçip alanları içeride okumak kutulamaz (düzeltici tur 2). */
export function sphereHitsFighter(c: Vec3, r: number, b: Fighter): boolean {
  const cx = c.x, cy = c.y, cz = c.z;
  const rr = r + HURT_R;
  let ax: number, ay: number, az: number, bx: number, by: number, bz: number;
  if (isLyingState(b.state)) {
    const fx = Math.sin(b.yaw), fz = Math.cos(b.yaw);
    ax = b.pos.x - fx * LYING_HURT_HALF; az = b.pos.z - fz * LYING_HURT_HALF; ay = b.pos.y + LYING_HURT_Y;
    bx = b.pos.x + fx * LYING_HURT_HALF; bz = b.pos.z + fz * LYING_HURT_HALF; by = ay;
  } else {
    ax = b.pos.x; az = b.pos.z; ay = b.pos.y + HURT_Y0;
    bx = b.pos.x; bz = b.pos.z; by = b.pos.y + HURT_Y1;
  }
  // segman AB üzerinde P'ye en yakın nokta
  const dx = bx - ax, dy = by - ay, dz = bz - az;
  const len2 = dx * dx + dy * dy + dz * dz;
  let t = 0;
  if (len2 > 1e-12) { t = ((cx - ax) * dx + (cy - ay) * dy + (cz - az) * dz) / len2; t = t < 0 ? 0 : t > 1 ? 1 : t; }
  const px = ax + dx * t - cx, py = ay + dy * t - cy, pz = az + dz * t - cz;
  return px * px + py * py + pz * pz <= rr * rr;
}

/** Koni testi: hedef merkezi A'nın ileri eksenine göre ±cone/2 içinde mi (tam açı derece). cone ≥ 360 → her zaman true. */
export function inCone(a: Fighter, b: Fighter, coneDeg: number): boolean {
  if (coneDeg >= 360) return true;
  const dx = b.pos.x - a.pos.x, dz = b.pos.z - a.pos.z;
  const d = hyp(dx, dz);
  if (d < 1e-6) return true;
  const dot = (dx * Math.sin(a.yaw) + dz * Math.cos(a.yaw)) / d;
  return dot >= Math.cos((coneDeg * 0.5) * Math.PI / 180);
}
